import { useAuth } from "@/contexts/AuthContext";
import { useTeamId } from "@/hooks/useTeamId";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface Props {
  moduleId: string;
}

function useFetchProgress(moduleId: string, userId?: string) {
  return useQuery({
    queryKey: ["assessment-progress", moduleId, userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("assessment_progress")
        .select("answers, completion_percentage, updated_at")
        .eq("user_id", userId!)
        .eq("module_id", moduleId)
        .maybeSingle();
      return data;
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
}

function useFetchModuleCompletion(moduleId: string, userId?: string) {
  return useQuery({
    queryKey: ["assessment-module-completion", moduleId, userId],
    queryFn: async () => {
      const { data } = await supabase
        .from("assessment_modules")
        .select("completion_date")
        .eq("user_id", userId!)
        .eq("module_id", moduleId)
        .eq("status", "complete")
        .maybeSingle();
      return data;
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
}

// ── Fallback ──
function CompleteFallback() {
  return (
    <div className="flex items-center gap-1.5">
      <CheckCircle2 className="h-4 w-4 text-[#0d9488]" />
      <span className="text-sm font-medium text-[#0d9488]">Assessment complete</span>
    </div>
  );
}

// ── Snapshot ──
function SnapshotContent({ userId }: { userId: string }) {
  const { data } = useFetchModuleCompletion("owner-readiness", userId);
  if (!data?.completion_date) return <CompleteFallback />;
  const formatted = new Date(data.completion_date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  return <p className="text-sm text-muted-foreground text-center">Completed {formatted}</p>;
}

// ── Timeline ──
function TimelineContent({ userId }: { userId: string }) {
  const { effectiveTeamId } = useTeamId();
  const { data: module } = useFetchModuleCompletion("owner-readiness-exit-timeline", userId);

  // Primary source: teams.succession_target_date
  const { data: team } = useQuery({
    queryKey: ["team-succession-date", effectiveTeamId],
    queryFn: async () => {
      const { data: row } = await supabase
        .from("teams")
        .select("succession_target_date")
        .eq("id", effectiveTeamId!)
        .maybeSingle();
      return row;
    },
    enabled: !!effectiveTeamId,
    staleTime: 30_000,
  });

  // Fallback: assessment_progress.answers.target_exit_date
  const { data: progressData } = useQuery({
    queryKey: ["a2-target-date", userId],
    queryFn: async () => {
      const { data: row } = await supabase
        .from("assessment_progress")
        .select("answers")
        .eq("user_id", userId)
        .eq("module_id", "owner-readiness-exit-timeline")
        .maybeSingle();
      return row;
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  const rawDate = team?.succession_target_date
    || (progressData?.answers as Record<string, unknown> | null)?.target_exit_date as string | undefined;

  if (!rawDate) {
    return (
      <div className="flex flex-col items-center gap-2">
        <p className="text-sm text-muted-foreground">No target date set yet</p>
      </div>
    );
  }

  const target = new Date(rawDate);
  const now = new Date();
  const start = module?.completion_date ? new Date(module.completion_date) : now;
  const totalMs = target.getTime() - start.getTime();
  const elapsedMs = now.getTime() - start.getTime();
  const pct = totalMs > 0 ? Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100)) : 100;
  const yearsRemaining = Math.max(0, (target.getTime() - now.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
  const isOverdue = target.getTime() < now.getTime();

  const label = target.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="space-y-2 w-full">
      <p className="text-sm font-semibold text-foreground text-center">Target transition: {label}</p>
      <Progress value={pct} className="h-2" />
      <p className="text-xs text-muted-foreground text-center">
        {isOverdue ? "Transition date reached" : `${yearsRemaining.toFixed(1)} years remaining`}
      </p>
    </div>
  );
}

// ── Business Profile ──
const HOUSEHOLD_LABELS: Record<string, string> = {
  under_50: "Under 50",
  "50_100": "50–100",
  "100_250": "100–250",
  "250_500": "250–500",
  "500_plus": "500+",
};

function BusinessProfileContent({ userId }: { userId: string }) {
  const { data } = useFetchProgress("business-profile", userId);
  if (!data?.answers) return <CompleteFallback />;
  const answers = data.answers as Record<string, any>;
  const raw = answers["client_household_count"] || answers["total_clients"] || answers["client_households"];
  if (!raw) return <CompleteFallback />;
  const display = HOUSEHOLD_LABELS[String(raw)] ?? String(raw);
  return (
    <div className="flex flex-col items-center">
      <span className="text-[28px] font-bold text-foreground">{display}</span>
      <span className="text-xs text-muted-foreground">Proudly supporting Canadian Households</span>
    </div>
  );
}

// ── Business Valuation ──
function BusinessValuationContent({ userId }: { userId: string }) {
  const { effectiveTeamId } = useTeamId();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["valuation-latest", userId, effectiveTeamId],
    queryFn: async () => {
      const { data: row } = await supabase
        .from("valuation_results")
        .select("valuation_amount, methodology")
        .eq("user_id", userId)
        .eq("is_current", true)
        .order("calculated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return row;
    },
    enabled: !!userId,
    staleTime: 30_000,
  });

  if (!data) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate("/assessment")}
          className="border-primary text-primary hover:bg-primary/5"
        >
          Run your valuation →
        </Button>
      </div>
    );
  }

  const formatted = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(data.valuation_amount);

  const METHODOLOGY_LABELS: Record<string, string> = {
    revenue_multiple: "Revenue Multiple",
    ebitda_multiple: "EBITDA Multiple",
    discounted_cashflow: "Discounted Cash Flow",
    asset_based: "Asset Based",
    aum_multiple: "AUM Multiple",
    blended: "Blended",
  };
  const methodLabel = data.methodology
    ? METHODOLOGY_LABELS[data.methodology] ??
      data.methodology.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase())
    : undefined;

  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-2xl font-bold text-foreground">{formatted}</span>
      <span className="text-xs text-muted-foreground">Estimated practice value</span>
      {methodLabel && (
        <span className="text-[11px] text-muted-foreground/70">{methodLabel}</span>
      )}
    </div>
  );
}

// ── Optimization ──
function OptimizationContent({ userId }: { userId: string }) {
  const { data } = useFetchProgress("business-optimization", userId);
  if (!data?.answers) return <CompleteFallback />;
  const answers = data.answers as Record<string, any>;
  const opp = answers["top_opportunity"];
  if (!opp) return <CompleteFallback />;
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-sm font-medium text-foreground">Top opportunity identified</span>
      <span className="text-xs text-muted-foreground line-clamp-2 text-center">{String(opp)}</span>
    </div>
  );
}

// ── Advisory Team ──
function AdvisoryTeamContent({ userId }: { userId: string }) {
  const { effectiveTeamId } = useTeamId();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ["advisory-team-count", effectiveTeamId],
    queryFn: async () => {
      const { count } = await supabase
        .from("team_members")
        .select("id", { count: "exact", head: true })
        .eq("team_id", effectiveTeamId!)
        .eq("member_type", "external");
      return count ?? 0;
    },
    enabled: !!effectiveTeamId,
    staleTime: 30_000,
  });

  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-3xl font-bold text-foreground">{data ?? 0}</span>
      <span className="text-xs text-muted-foreground">Advisors supporting your succession</span>
      <Button
        size="sm"
        variant="outline"
        onClick={() => navigate("/team")}
        className="mt-1 border-primary text-primary hover:bg-primary/5 text-xs"
      >
        Update →
      </Button>
    </div>
  );
}

// ── Financial Readiness ──
function FinancialReadinessContent({ userId }: { userId: string }) {
  const { data } = useFetchProgress("financial-readiness", userId);
  if (!data?.answers) return <CompleteFallback />;
  const answers = data.answers as Record<string, any>;
  const score = answers["readiness_score"] || answers["overall_score"];
  
  let label = "On Track";
  let colorClass = "text-[#16a34a]";
  if (score != null) {
    const n = Number(score);
    if (n < 40) { label = "Needs Attention"; colorClass = "text-[#ef4444]"; }
    else if (n < 60) { label = "Needs Attention"; colorClass = "text-amber-500"; }
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`text-lg font-bold ${colorClass}`}>{label}</span>
      <span className="text-xs text-muted-foreground">Retirement readiness</span>
    </div>
  );
}

// ── Successor Strategy ──
function SuccessorStrategyContent({ userId }: { userId: string }) {
  const { data } = useFetchProgress("successor-strategy", userId);
  if (!data?.answers) return <CompleteFallback />;
  const answers = data.answers as Record<string, any>;
  const strategy = answers["succession_type"] || answers["strategy_type"];
  if (!strategy) return <CompleteFallback />;
  const label = String(strategy).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-sm font-semibold text-foreground">{label}</span>
      <span className="text-xs text-muted-foreground">Selected succession strategy</span>
    </div>
  );
}

// ── Governance ──
function GovernanceContent({ userId }: { userId: string }) {
  const { data } = useFetchProgress("governance-family", userId);
  if (!data?.answers) return <CompleteFallback />;
  const answers = data.answers as Record<string, any>;
  // Count truthy governance structure keys
  const govKeys = Object.keys(answers).filter(
    (k) => k.includes("governance") || k.includes("structure")
  );
  const count = govKeys.filter((k) => !!answers[k]).length || 0;
  return (
    <div className="flex flex-col items-center gap-1">
      <span className="text-3xl font-bold text-foreground">{count}</span>
      <span className="text-xs text-muted-foreground">Governance structures in place</span>
    </div>
  );
}

// ── Exit Structure ──
function ExitStructureContent({ userId }: { userId: string }) {
  const { data } = useFetchProgress("exit-structure", userId);
  if (!data?.answers) return <CompleteFallback />;
  const answers = data.answers as Record<string, any>;
  const score = answers["readiness_score"] || answers["overall_score"];

  let label = "On Track";
  let colorClass = "text-[#16a34a]";
  if (score != null) {
    const n = Number(score);
    if (n < 40) { label = "Needs Attention"; colorClass = "text-[#ef4444]"; }
    else if (n < 60) { label = "In Progress"; colorClass = "text-amber-500"; }
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <span className={`text-lg font-bold ${colorClass}`}>{label}</span>
      <span className="text-xs text-muted-foreground">Tax structure readiness</span>
    </div>
  );
}

// ── Plan Assembly ──
function PlanAssemblyContent() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1.5">
        <CheckCircle2 className="h-5 w-5 text-[#0d9488]" />
        <span className="text-sm font-bold text-[#0d9488]">Plan complete</span>
      </div>
      <Button
        size="sm"
        onClick={() => navigate("/plan")}
        className="gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90"
      >
        View your Plan →
      </Button>
    </div>
  );
}

// ── Main dispatcher ──
export function AssessmentTileContent({ moduleId }: Props) {
  const { user } = useAuth();
  if (!user?.id) return <CompleteFallback />;

  switch (moduleId) {
    case "owner-readiness":
      return <SnapshotContent userId={user.id} />;
    case "owner-readiness-exit-timeline":
      return <TimelineContent userId={user.id} />;
    case "business-profile":
      return <BusinessProfileContent userId={user.id} />;
    case "business-valuation":
      return <BusinessValuationContent userId={user.id} />;
    case "business-optimization":
      return <OptimizationContent userId={user.id} />;
    case "advisory-team":
      return <AdvisoryTeamContent userId={user.id} />;
    case "financial-readiness":
      return <FinancialReadinessContent userId={user.id} />;
    case "successor-strategy":
      return <SuccessorStrategyContent userId={user.id} />;
    case "governance-family":
      return <GovernanceContent userId={user.id} />;
    case "exit-structure":
      return <ExitStructureContent userId={user.id} />;
    case "mysuccession-plan":
      return <PlanAssemblyContent />;
    default:
      return <CompleteFallback />;
  }
}
